import { useState } from 'react';
import { MapPin } from 'lucide-react';
import { api } from '@/lib/api';
import { SitePage, COMPANY } from './SiteChrome';
import { useI18n } from '@/lib/i18n';
import { useSession } from '@/lib/session';
import { Input } from '@/ui';
import { SendLinkControl, composeBody, type MailDraft } from '@/ui/send';
import { trackClick } from './metrics';
import { Reveal } from './reveal';
import { DemoAccess } from './DemoAccess';
import './pages.css';

/**
 * Contact. The page composes the visitor's first mail for them (redesigned 27 Oct
 * 2026; the owner found the two-box layout sparse and the draft a list of labels to
 * type). They choose what the mail is about, may add an organisation and a line of
 * context, watch the message take shape, and press one button. It is still only a
 * `mailto:`: what they type is placed in the URL of that one link and goes nowhere
 * else — not posted, not stored — so there is no form and no privacy question.
 *
 * The topics are the real reasons someone lands here. Every "Request access" and
 * "Start free" button on the site points at this page (there is no self-serve
 * sign-up), so asking for access is the first and default topic.
 *
 * Before the redesign, "Talk to us" opened the visitor's own mail program with
 * a ready-to-use draft addressed to HLSC — the same `mailto:` pattern the
 * product uses to share a link (ui/send.tsx, lib/mail-drafts.ts) — and, in the
 * same click, files a row for the operator console.
 *
 * The visitor writes their details into the mail, not into a form, so the row
 * carries none (src/api/leads.js, kind 'contact-mail': it says a draft was
 * opened and in which language, and folds repeats into the open row). It is a
 * signal, not the enquiry: the enquiry is the mail. Filing is fire-and-forget
 * and silent — the mail program has already opened, so a failed post must
 * never put an error in front of the visitor — and happens once per tab.
 */
const FILED_KEY = 'hs.contact.filed';

type Topic = 'access' | 'selection' | 'security' | 'pricing';
const TOPICS: Topic[] = ['access', 'selection', 'security', 'pricing'];


export default function Contact() {
  const { t, lang, fill } = useI18n();
  const { config } = useSession();
  const c = t.site.contact;
  const [topic, setTopic] = useState<Topic>('access');
  const [organisation, setOrganisation] = useState('');
  const [context, setContext] = useState('');

  const topicWords: Record<Topic, { label: string; subject: string; intro: string }> = {
    access: { label: c.topicAccess, subject: c.subjectAccess, intro: c.introAccess },
    selection: { label: c.topicSelection, subject: c.subjectSelection, intro: c.introSelection },
    security: { label: c.topicSecurity, subject: c.subjectSecurity, intro: c.introSecurity },
    pricing: { label: c.topicPricing, subject: c.subjectPricing, intro: c.introPricing },
  };

  // The mail is built from what is on screen, so the preview and the link cannot differ:
  // both read this one draft. Empty optional lines are left out, never sent as bare labels.
  const draft: MailDraft = {
    subject: topicWords[topic].subject,
    intro: [
      t.share.helloAnon,
      topicWords[topic].intro,
      ...(organisation.trim() ? [fill(c.organisationLine, { value: organisation.trim() })] : []),
      ...(context.trim() ? [context.trim()] : []),
    ],
    link: '',
    ask: [c.signOff],
  };

  function fileOpenedDraft() {
    trackClick('contact_mail', location.pathname);
    try {
      if (sessionStorage.getItem(FILED_KEY)) return;
    } catch {
      // Storage blocked: file anyway; the server folds repeats.
    }
    api
      .lead({ kind: 'contact-mail', locale: lang, source: 'contact-page', website: '' })
      .then(() => {
        try {
          sessionStorage.setItem(FILED_KEY, '1');
        } catch {
          // Nothing to remember it with; the server folds repeats.
        }
      })
      .catch(() => {
        // Deliberately nothing: see above.
      });
  }

  return (
    <SitePage>
      <section className="site-section contact-page">
        <div className="site-shell">
          <Reveal>
            <div className="section-head">
              <h1 className="section-title">{c.title}</h1>
            </div>
          </Reveal>

          <div className="contact-compose">
            <div className="contact-steps">
              <fieldset className="contact-step">
                <legend className="contact-step-title">
                  <span className="contact-step-n">1</span>
                  {c.stepTopic}
                </legend>
                <div className="contact-topics">
                  {TOPICS.map((id) => (
                    <label key={id} className={topic === id ? 'contact-topic is-active' : 'contact-topic'}>
                      <input
                        type="radio"
                        name="contact-topic"
                        value={id}
                        checked={topic === id}
                        onChange={() => setTopic(id)}
                      />
                      <span>{topicWords[id].label}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <div className="contact-step">
                <p className="contact-step-title">
                  <span className="contact-step-n">2</span>
                  {c.stepDetails}
                  <span className="contact-optional">{c.optional}</span>
                </p>
                <div className="contact-fields">
                  <label className="contact-field">
                    <span>{c.organisation}</span>
                    <Input value={organisation} maxLength={120} autoComplete="organization" onChange={(event) => setOrganisation(event.target.value)} />
                  </label>
                  <label className="contact-field">
                    <span>{c.context}</span>
                    <Input value={context} maxLength={300} onChange={(event) => setContext(event.target.value)} />
                  </label>
                </div>
              </div>
            </div>

            {/* The message as it will leave: a sheet, not a card, so the page reads as
              * "this is what you are about to send". The recipient is named, not
              * addressed — the address appears once, in the line shown after the click. */}
            <aside className="contact-sheet" aria-label={c.sheetTitle}>
              <p className="contact-step-title">
                <span className="contact-step-n">3</span>
                {c.sheetTitle}
              </p>
              <dl className="contact-sheet-head">
                <dt>{c.sheetTo}</dt>
                <dd>{c.officeTitle}</dd>
                <dt>{c.sheetSubject}</dt>
                <dd data-testid="contact-subject">{draft.subject}</dd>
              </dl>
              <pre className="contact-sheet-body" data-testid="contact-body">{composeBody(draft).replace(/\r\n/g, '\n')}</pre>
              <SendLinkControl
                variant="primary"
                onSend={fileOpenedDraft}
                label={c.send}
                recipient={COMPANY.email}
                draft={draft}
                fallbackNote={fill(t.share.contactFallback, { email: COMPANY.email })}
              />
            </aside>
          </div>

          <div className="contact-aside">
            {config?.demo !== false ? (
              <div className="contact-demo">
                <h2 className="stat-label">{c.demoTitle}</h2>
                <DemoAccess label={t.site.hero.ctaSecondary} size="md" />
              </div>
            ) : null}
            <p className="muted contact-address">
              <MapPin size={16} />
              <span>
                {c.officeTitle}
                <br />
                {COMPANY.street}, {COMPANY.city}, {COMPANY.country}
              </span>
            </p>
          </div>
        </div>
      </section>
    </SitePage>
  );
}
